import { Capacitor } from '@capacitor/core';

export type AuthProvider = 'google' | 'apple';

/**
 * Returns true if the current environment is detected as an Apple device
 * (iOS, iPadOS, or macOS), either natively via Capacitor or in a web browser.
 */
export function isAppleDevice(): boolean {
  try {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return true;
    }
  } catch {
    // Ignore Capacitor access errors and fall back to browser detection
  }

  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || '';

  // Check iOS devices (iPhone, iPad, iPod)
  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return true;
  }

  // Check iPadOS where Safari reports Macintosh desktop user agent but has touch points
  if (platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1) {
    return true;
  }

  // Check macOS (Macintosh desktop or laptop)
  if (/Macintosh|Mac OS X/i.test(userAgent) || /^Mac/i.test(platform)) {
    return true;
  }

  return false;
}

/**
 * Returns true if the current environment is detected as an Android device,
 * either natively via Capacitor or in a web browser.
 */
export function isAndroidDevice(): boolean {
  try {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      return true;
    }
  } catch {
    // Ignore Capacitor access errors and fall back to browser detection
  }

  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || '';

  return /Android/i.test(userAgent) || /Android/i.test(platform);
}

/**
 * Returns the authentication providers in the preferred display order.
 * - Apple devices: ['apple', 'google']
 * - Android devices: ['google', 'apple']
 * - Other / fallback: ['google', 'apple']
 */
export function getAuthProvidersOrder(): AuthProvider[] {
  if (isAppleDevice()) {
    return ['apple', 'google'];
  }
  return ['google', 'apple'];
}
